import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { supabase } from './supabaseClient';
import './Android.css';

// Ordered stages for the "reserve a username" path. Each is its own page.
const RESERVE_STEPS = ['phone', 'code', 'username', 'email'];

// GoTrue's SMS throttle. Matches the [auth.sms] default in
// supabase/config.toml, which does not override max_frequency.
const RESEND_SECONDS = 60;

// Mirrors the check constraint on profiles.username -- say so here rather
// than let the database be the one to explain it.
const USERNAME_RE = /^[a-z0-9]{2,20}$/;

// How long the typing has to stop before we ask the server whether a name is
// free. Long enough that a name is not looked up letter by letter, short
// enough that the answer lands before the finger reaches Continue.
const USERNAME_DEBOUNCE_MS = 350;

// Mirrors the check on android_waitlist.email. Deliberately loose: a typo
// guard, not an RFC 5322 parser.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Reads what was typed as a number belonging to `iso`, or nothing if it is
// not a real number there. Returns null rather than throwing.
//
// Parsing, not string surgery, because the input is not predictable. Someone
// on Australia may type "0401 119 114", "401119114", or paste
// "+61 401 119 114" -- the first needs its trunk 0 dropped and the last must
// not have 61 put in front of it a second time. Italy meanwhile keeps the
// leading 0 that Australia drops. libphonenumber knows all three; a regex
// that tried would just be libphonenumber with the metadata missing.
//
// An explicit country code in the input wins over `iso`, which is what makes
// a pasted international number survive a mismatched picker.
const parsePhone = (iso, typed) => {
  const parsed = parsePhoneNumberFromString(typed || '', iso);
  return parsed && parsed.isValid() ? parsed : null;
};

function messageFor(error) {
  if (!error) return 'Something went wrong. Try again.';
  const msg = error.message || '';
  if (/rate|too many|seconds|security purposes/i.test(msg)) {
    return 'Too many attempts. Wait a minute and try again.';
  }
  return msg || 'Something went wrong. Try again.';
}

// Countries offered by the phone step, as ISO 3166-1 alpha-2 -- the form
// libphonenumber parses against, and unambiguous where a dial code is not
// (+1 is both US and Canada). Dial codes are read out of its metadata rather
// than written here, so the label on an option and the rules used to parse
// what is typed under it cannot drift apart.
const COUNTRIES = [
  { n: 'Afghanistan', c: 'AF' },
  { n: 'Albania', c: 'AL' },
  { n: 'Algeria', c: 'DZ' },
  { n: 'Argentina', c: 'AR' },
  { n: 'Australia', c: 'AU' },
  { n: 'Austria', c: 'AT' },
  { n: 'Bangladesh', c: 'BD' },
  { n: 'Belgium', c: 'BE' },
  { n: 'Brazil', c: 'BR' },
  { n: 'Bulgaria', c: 'BG' },
  { n: 'Cambodia', c: 'KH' },
  { n: 'Canada', c: 'CA' },
  { n: 'Chile', c: 'CL' },
  { n: 'China', c: 'CN' },
  { n: 'Colombia', c: 'CO' },
  { n: 'Croatia', c: 'HR' },
  { n: 'Czechia', c: 'CZ' },
  { n: 'Denmark', c: 'DK' },
  { n: 'Egypt', c: 'EG' },
  { n: 'Estonia', c: 'EE' },
  { n: 'Finland', c: 'FI' },
  { n: 'France', c: 'FR' },
  { n: 'Germany', c: 'DE' },
  { n: 'Greece', c: 'GR' },
  { n: 'Hong Kong', c: 'HK' },
  { n: 'Hungary', c: 'HU' },
  { n: 'Iceland', c: 'IS' },
  { n: 'India', c: 'IN' },
  { n: 'Indonesia', c: 'ID' },
  { n: 'Ireland', c: 'IE' },
  { n: 'Israel', c: 'IL' },
  { n: 'Italy', c: 'IT' },
  { n: 'Japan', c: 'JP' },
  { n: 'Jordan', c: 'JO' },
  { n: 'Kenya', c: 'KE' },
  { n: 'Malaysia', c: 'MY' },
  { n: 'Mexico', c: 'MX' },
  { n: 'Nepal', c: 'NP' },
  { n: 'Netherlands', c: 'NL' },
  { n: 'New Zealand', c: 'NZ' },
  { n: 'Nigeria', c: 'NG' },
  { n: 'Norway', c: 'NO' },
  { n: 'Pakistan', c: 'PK' },
  { n: 'Peru', c: 'PE' },
  { n: 'Philippines', c: 'PH' },
  { n: 'Poland', c: 'PL' },
  { n: 'Portugal', c: 'PT' },
  { n: 'Qatar', c: 'QA' },
  { n: 'Romania', c: 'RO' },
  { n: 'Russia', c: 'RU' },
  { n: 'Saudi Arabia', c: 'SA' },
  { n: 'Singapore', c: 'SG' },
  { n: 'South Africa', c: 'ZA' },
  { n: 'South Korea', c: 'KR' },
  { n: 'Spain', c: 'ES' },
  { n: 'Sri Lanka', c: 'LK' },
  { n: 'Sweden', c: 'SE' },
  { n: 'Switzerland', c: 'CH' },
  { n: 'Taiwan', c: 'TW' },
  { n: 'Thailand', c: 'TH' },
  { n: 'Turkey', c: 'TR' },
  { n: 'Ukraine', c: 'UA' },
  { n: 'United Arab Emirates', c: 'AE' },
  { n: 'United Kingdom', c: 'GB' },
  { n: 'United States', c: 'US' },
  { n: 'Vietnam', c: 'VN' },
]
  .map((c) => ({ ...c, d: `+${getCountryCallingCode(c.c)}` }))
  .sort((a, b) => a.n.localeCompare(b.n));

// The default, and the fallback if data.country is ever something the list
// does not hold. Guaranteed to be in it.
const DEFAULT_COUNTRY = 'Australia';

/* Username reservation mark (an "@" sign). Decorative. */
function AtIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="3.4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M15.4 12v1.6a2.6 2.6 0 0 0 5.1 0V12a8.5 8.5 0 1 0-3.3 6.7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Notification mark (a bell). Decorative. */
function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M6 9.5a6 6 0 0 1 12 0c0 4.6 1.8 5.9 1.8 5.9H4.2S6 14.1 6 9.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 19a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Dots({ total, active }) {
  return (
    <ol className="an-dots" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <li
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className={
            'an-dot' +
            (i === active ? ' is-active' : '') +
            (i < active ? ' is-done' : '')
          }
        />
      ))}
    </ol>
  );
}

function AgreementLine() {
  return (
    <p className="an-agreement">
      By continuing, you agree to our <Link to="/terms">Terms of Service</Link>{' '}
      and <Link to="/privacy">Privacy Policy</Link>.
    </p>
  );
}

export default function Android() {
  // flow: 'choose' | 'reserve' | 'notify'. step indexes RESERVE_STEPS.
  const [flow, setFlow] = useState('choose');
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    country: 'Australia',
    phoneNumber: '',
    code: '',
    username: '',
    email: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Live availability for the username screen. Only ever a hint: the UNIQUE
  // index on profiles.username is the real arbiter and two people can always
  // race between this answer and the claim. Its job is to stop someone
  // typing a name, pressing Continue, and only then being told no.
  //
  // `value` is the name the status describes. Keystrokes outrun round-trips,
  // so without it a reply about an older name can be painted next to a newer
  // one -- "available" under a name nobody checked.
  const [nameCheck, setNameCheck] = useState({ status: 'idle', value: '' });

  // The name this account already holds, if it is coming back to change it.
  // Without it username_available() reports their own reservation as taken.
  const [claimedUsername, setClaimedUsername] = useState('');

  const inputRef = useRef(null);
  // Errors are cleared by whoever navigates, not here. Clearing on every step
  // change would wipe the message of any handler that reports a failure and
  // sends the user somewhere else -- which is exactly what the verify step
  // does when it turns an existing account away.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [flow, step, done]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const update = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const wantedUsername = data.username.trim().toLowerCase();
  const onUsernameStep =
    flow === 'reserve' && !done && RESERVE_STEPS[step] === 'username';

  // Ask the server whether the typed name is free, once the typing pauses.
  //
  // username_available() returns false for a malformed name as well as a
  // taken one, so the format is settled here first -- that way a `false` from
  // the server can only mean "taken" and the message can say so.
  useEffect(() => {
    if (!onUsernameStep) return undefined;

    if (!wantedUsername) {
      setNameCheck({ status: 'idle', value: '' });
      return undefined;
    }
    if (!USERNAME_RE.test(wantedUsername)) {
      setNameCheck({ status: 'invalid', value: wantedUsername });
      return undefined;
    }
    // Their own reservation. The server would call this taken, which is true
    // and useless.
    if (wantedUsername === claimedUsername) {
      setNameCheck({ status: 'free', value: wantedUsername });
      return undefined;
    }

    setNameCheck({ status: 'checking', value: wantedUsername });

    // The cleanup runs on the next keystroke, so `cancelled` covers the
    // request already in flight that clearTimeout is too late for.
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data: free, error: err } = await supabase.rpc(
        'username_available',
        { candidate: wantedUsername },
      );
      if (cancelled) return;
      // A failed lookup is not a refusal. 'unknown' says nothing on screen and
      // still lets them press Continue -- set_username() is the real arbiter
      // and will refuse if it must. Calling a name taken on a network blip
      // would send people away from one that is theirs for the asking.
      setNameCheck({
        status: err ? 'unknown' : free ? 'free' : 'taken',
        value: wantedUsername,
      });
    }, USERNAME_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [onUsernameStep, wantedUsername, claimedUsername]);

  // Anything the status has not caught up with reads as still checking, which
  // also keeps Continue disabled until there is a real answer.
  const nameStatus =
    nameCheck.value === wantedUsername ? nameCheck.status : 'checking';

  const canClaimName = nameStatus === 'free' || nameStatus === 'unknown';

  const country =
    COUNTRIES.find((c) => c.n === data.country) ||
    COUNTRIES.find((c) => c.n === DEFAULT_COUNTRY);
  const dialCode = country.d;

  // null until what has been typed is a real number for the selected country.
  const phone = parsePhone(country.c, data.phoneNumber);

  // E.164 minus the '+'. That is how GoTrue stores a number, and the format
  // the [auth.sms.test_otp] keys in supabase/config.toml are written in --
  // a number that misses that list is one a real provider is asked to text.
  const phoneE164 = phone ? phone.number.slice(1) : '';

  // Quoted back on the code and done screens. Once it parses, show it the way
  // libphonenumber groups it; before that, the raw digits are all we have.
  const fullPhone = phone
    ? phone.formatInternational()
    : `${dialCode} ${data.phoneNumber.trim()}`.trim();

  const startReserve = () => {
    setError('');
    setStep(0);
    setDone(false);
    // A restart may be a different phone number, so nothing carries over.
    setClaimedUsername('');
    setNameCheck({ status: 'idle', value: '' });
    setFlow('reserve');
  };

  const startNotify = () => {
    setError('');
    setDone(false);
    setFlow('notify');
  };

  const backToChoose = () => {
    setError('');
    setStep(0);
    setDone(false);
    setFlow('choose');
  };

  const back = () => {
    if (busy) return;
    setError('');
    // Once the code has been accepted the number is settled and the code is
    // spent, so the first two screens stop being somewhere to go back to.
    // From the username screen on, Back either steps within the flow or
    // leaves it entirely.
    const floor = step >= RESERVE_STEPS.indexOf('username') ? 2 : 0;
    if (flow === 'reserve' && !done && step > floor) {
      setStep(step - 1);
    } else {
      backToChoose();
    }
  };

  // Texting the code is also what creates the account: signInWithOtp inserts
  // the auth.users row with phone_confirmed_at still NULL. Nothing exists in
  // public.profiles until the code comes back verified.
  //
  // Whether an SMS is actually sent is not decided here -- it is the same
  // call the iOS app makes. Numbers registered as test numbers on the
  // Supabase project (Authentication -> Sign In / Providers -> Phone -> Test
  // OTP, or [auth.sms.test_otp] in config.toml locally) short-circuit inside
  // GoTrue: no provider is contacted, nothing is billed, and the fixed code
  // is what verifyOtp accepts.
  const sendCode = async () => {
    const { error: err } = await supabase.auth.signInWithOtp({
      phone: phoneE164,
      options: { shouldCreateUser: true },
    });
    if (err) return err;
    setCooldown(RESEND_SECONDS);
    return null;
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    setError('');
    const err = await sendCode();
    setBusy(false);
    if (err) setError(messageFor(err));
  };

  const submitReserve = async (e) => {
    e.preventDefault();
    const id = RESERVE_STEPS[step];

    if (id === 'phone') {
      // A number that does not parse is one an SMS would be spent failing to
      // reach, so the parser is the gate rather than a digit count.
      if (!phone) {
        setError(`Enter a valid phone number for ${country.n}.`);
        return;
      }
      setBusy(true);
      setError('');
      const err = await sendCode();
      setBusy(false);
      if (err) {
        setError(messageFor(err));
        return;
      }
      setStep(1);
      return;
    }

    if (id === 'code') {
      const token = data.code.trim();
      if (!/^\d{6}$/.test(token)) {
        setError('Enter the 6-digit code.');
        return;
      }
      setBusy(true);
      setError('');
      // Verifying stamps phone_confirmed_at, which fires
      // app.handle_phone_verified() and writes the profile row that
      // set_username() and the waitlist both need to point at.
      const { error: verifyErr } = await supabase.auth.verifyOtp({
        phone: phoneE164,
        token,
        type: 'sms',
      });
      if (verifyErr) {
        setBusy(false);
        setError('That code is not right, or it has expired.');
        return;
      }
      // Verified, which is the earliest this question may be asked: the RPC
      // decides whether this number belongs to an account built in the iOS
      // app rather than here, and marks it as ours if not.
      const { data: profile, error: startErr } = await supabase.rpc(
        'start_android_reservation',
      );
      if (startErr) {
        // Not a number we can take a reservation for. Sign out first --
        // verifying just opened a session on somebody's real account, in a
        // browser, on a page that is not the app.
        await supabase.auth.signOut();
        setBusy(false);
        setData((d) => ({ ...d, phoneNumber: '', code: '' }));
        setClaimedUsername('');
        setNameCheck({ status: 'idle', value: '' });
        setStep(0);
        setError(
          startErr.code === 'FP001'
            ? 'That number already has a Fampack account. You’ll be able to ' +
                'sign in with it when the Android app launches — try a ' +
                'different number.'
            : messageFor(startErr),
        );
        return;
      }
      // A name already on file means they reserved before and are here to
      // change it, so open both fields on what they already have.
      const held = profile?.username || '';
      setBusy(false);
      setClaimedUsername(held);
      if (held) update('username', held);
      if (profile?.email) update('email', profile.email);
      setStep(2);
      return;
    }

    if (id === 'username') {
      const wanted = data.username.trim().toLowerCase();
      if (!USERNAME_RE.test(wanted)) {
        setError('Usernames are 2–20 letters and numbers only.');
        return;
      }
      setBusy(true);
      setError('');
      // The name is taken here, not on the screen after it -- the UNIQUE
      // index on profiles.username is the arbiter, and waiting until the
      // email step would mean telling someone their name was gone after
      // they had already handed over another detail.
      const { error: rpcErr } = await supabase.rpc('set_username', {
        new_username: wanted,
      });
      setBusy(false);
      if (rpcErr) {
        setError(
          rpcErr.code === '23505'
            ? `@${wanted} has already been taken. Try another.`
            : messageFor(rpcErr),
        );
        return;
      }
      update('username', wanted);
      setStep(3);
      return;
    }

    // email
    const email = data.email.trim();
    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email.');
      return;
    }
    setBusy(true);
    setError('');
    // Signed in by now, so the RPC takes its "reserve" branch: the row is
    // linked to the profile holding the username just claimed.
    const { error: rpcErr } = await supabase.rpc('join_android_waitlist', {
      p_email: email,
    });
    setBusy(false);
    if (rpcErr) {
      setError(
        rpcErr.code === '23505'
          ? 'That email is already on the waitlist for another account.'
          : messageFor(rpcErr),
      );
      return;
    }
    update('email', email);
    setDone(true);
  };

  const submitNotify = async (e) => {
    e.preventDefault();
    const email = data.email.trim();
    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email.');
      return;
    }
    setBusy(true);
    setError('');
    // No session here, so the RPC takes its anonymous branch and stores the
    // address with no account attached. An address already on the list is a
    // silent no-op server-side, so this always reads as success -- there is
    // deliberately no way to use this form to ask who is on the waitlist.
    const { error: rpcErr } = await supabase.rpc('join_android_waitlist', {
      p_email: email,
    });
    setBusy(false);
    if (rpcErr) {
      setError(messageFor(rpcErr));
      return;
    }
    update('email', email);
    setDone(true);
  };

  const showDots = flow === 'reserve' && !done;

  let body;
  if (flow === 'choose') {
    body = (
      <div className="an-card an-card--wide" key="choose">
        <h1 className="an-title">Fampack for Android</h1>
        <p className="an-sub">
          The Android app is on its way. Pick how you&apos;d like to get in
          early.
        </p>
        <div className="an-choices">
          <div className="an-choice">
            <div className="an-choice__icon">
              <AtIcon />
            </div>
            <h2 className="an-choice__title">Reserve your unique username</h2>
            <p className="an-choice__desc">
              Verify your phone number with a text code, then lock in the unique
              username you want before the app launches.
            </p>
            <button
              type="button"
              className="an-btn an-btn--primary an-choice__btn"
              onClick={startReserve}
            >
              Reserve my username
            </button>
          </div>
          <div className="an-choice">
            <div className="an-choice__icon">
              <BellIcon />
            </div>
            <h2 className="an-choice__title">Just notify me</h2>
            <p className="an-choice__desc">
              Only want a heads-up? Leave your email and we&apos;ll message you
              the moment the Android app goes live.
            </p>
            <button
              type="button"
              className="an-btn an-btn--primary an-choice__btn"
              onClick={startNotify}
            >
              Notify me
            </button>
          </div>
        </div>
        <Link className="an-textlink" to="/">
          Back to home
        </Link>
      </div>
    );
  } else if (flow === 'reserve') {
    if (done) {
      body = (
        <div className="an-card" key="reserve-done">
          <div className="an-check" aria-hidden="true">
            ✓
          </div>
          <h1 className="an-title">Username reserved</h1>
          <p className="an-help">
            <strong>@{data.username.trim()}</strong> is yours. We&apos;ll email{' '}
            {data.email} and text {fullPhone} when the Android app is ready so
            you can claim it.
          </p>
          <div className="an-actions">
            <Link className="an-btn an-btn--primary" to="/">
              Back to home
            </Link>
          </div>
        </div>
      );
    } else {
      const id = RESERVE_STEPS[step];
      if (id === 'phone') {
        body = (
          <form className="an-card" key="phone" onSubmit={submitReserve}>
            <p className="an-count">
              Step {step + 1} of {RESERVE_STEPS.length}
            </p>
            <h1 className="an-title">What&apos;s your phone number?</h1>
            <div className="an-phone">
              <label className="an-sr" htmlFor="an-country">
                Country
              </label>
              <select
                id="an-country"
                className="an-select"
                value={data.country}
                onChange={(e) => update('country', e.target.value)}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.n} value={c.n}>
                    {c.n} ({c.d})
                  </option>
                ))}
              </select>
              <label className="an-sr" htmlFor="an-phone">
                Phone number
              </label>
              <input
                ref={inputRef}
                id="an-phone"
                className="an-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="412 345 678"
                value={data.phoneNumber}
                onChange={(e) => update('phoneNumber', e.target.value)}
              />
            </div>
            <p className="an-help">
              We&apos;ll text you a 6-digit code to confirm it&apos;s really you.
            </p>
            {error && <p className="an-error">{error}</p>}
            <div className="an-actions">
              <button
                type="button"
                className="an-btn an-btn--ghost"
                onClick={back}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="submit"
                className="an-btn an-btn--primary"
                disabled={busy}
              >
                {busy ? 'Sending…' : 'Send code'}
              </button>
            </div>
          </form>
        );
      } else if (id === 'code') {
        body = (
          <form className="an-card" key="code" onSubmit={submitReserve}>
            <p className="an-count">
              Step {step + 1} of {RESERVE_STEPS.length}
            </p>
            <h1 className="an-title">Enter your code</h1>
            <label className="an-sr" htmlFor="an-code">
              Verification code
            </label>
            <input
              ref={inputRef}
              id="an-code"
              className="an-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={data.code}
              onChange={(e) => update('code', e.target.value)}
            />
            <p className="an-help">
              {data.phoneNumber
                ? `We sent a 6-digit code to ${fullPhone}.`
                : 'Enter the 6-digit code we texted you.'}
            </p>
            {error && <p className="an-error">{error}</p>}
            <div className="an-actions">
              <button
                type="button"
                className="an-btn an-btn--ghost"
                onClick={back}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="submit"
                className="an-btn an-btn--primary"
                disabled={busy}
              >
                {busy ? 'Verifying…' : 'Verify'}
              </button>
            </div>
            <button
              type="button"
              className="an-textlink"
              onClick={resend}
              disabled={busy || cooldown > 0}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send a new code'}
            </button>
          </form>
        );
      } else if (id === 'username') {
        body = (
          <form className="an-card" key="username" onSubmit={submitReserve}>
            <p className="an-count">
              Step {step + 1} of {RESERVE_STEPS.length}
            </p>
            <h1 className="an-title">Claim your username</h1>
            <label className="an-sr" htmlFor="an-username">
              Username
            </label>
            <div className="an-inputgroup">
              <span className="an-inputgroup__prefix" aria-hidden="true">
                @
              </span>
              <input
                ref={inputRef}
                id="an-username"
                className="an-input an-input--prefixed"
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck="false"
                maxLength={20}
                placeholder="yourname"
                value={data.username}
                onChange={(e) =>
                  update(
                    'username',
                    e.target.value.replace(/[^A-Za-z0-9]/g, '').toLowerCase(),
                  )
                }
                aria-describedby="an-username-status"
              />
            </div>
            <p
              className={`an-status is-${nameStatus}`}
              id="an-username-status"
              aria-live="polite"
            >
              {nameStatus === 'checking' && `Checking @${wantedUsername}…`}
              {nameStatus === 'invalid' &&
                'Usernames are 2–20 lowercase letters and numbers.'}
              {nameStatus === 'taken' && `@${wantedUsername} is already taken.`}
              {nameStatus === 'free' &&
                (wantedUsername === claimedUsername
                  ? `@${wantedUsername} is already yours.`
                  : `@${wantedUsername} is available.`)}
            </p>
            <p className="an-help">
              2–20 lowercase letters and numbers. Claiming it here holds it
              against your phone number.
            </p>
            {error && <p className="an-error">{error}</p>}
            <div className="an-actions">
              <button
                type="button"
                className="an-btn an-btn--ghost"
                onClick={back}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="submit"
                className="an-btn an-btn--primary"
                disabled={busy || !canClaimName}
              >
                {busy ? 'Claiming…' : 'Continue'}
              </button>
            </div>
          </form>
        );
      } else {
        body = (
          <form className="an-card" key="reserve-email" onSubmit={submitReserve}>
            <p className="an-count">
              Step {step + 1} of {RESERVE_STEPS.length}
            </p>
            <h1 className="an-title">What&apos;s your email?</h1>
            <label className="an-sr" htmlFor="an-reserve-email">
              Email
            </label>
            <input
              ref={inputRef}
              id="an-reserve-email"
              className="an-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={data.email}
              onChange={(e) => update('email', e.target.value)}
            />
            <p className="an-help">
              We&apos;ll email you when the Android app is ready to claim your
              username.
            </p>
            {error && <p className="an-error">{error}</p>}
            <div className="an-actions">
              <button
                type="button"
                className="an-btn an-btn--ghost"
                onClick={back}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="submit"
                className="an-btn an-btn--primary"
                disabled={busy}
              >
                {busy ? 'Saving…' : 'Reserve username'}
              </button>
            </div>
          </form>
        );
      }
    }
  } else if (done) {
    body = (
      <div className="an-card" key="notify-done">
        <div className="an-check" aria-hidden="true">
          ✓
        </div>
        <h1 className="an-title">You&apos;re on the list</h1>
        <p className="an-help">
          Thanks! We&apos;ll email <strong>{data.email}</strong> the moment the
          Fampack Android app is live.
        </p>
        <div className="an-actions">
          <Link className="an-btn an-btn--primary" to="/">
            Back to home
          </Link>
        </div>
      </div>
    );
  } else {
    body = (
      <form className="an-card" key="email" onSubmit={submitNotify}>
        <h1 className="an-title">Get notified at launch</h1>
        <label className="an-sr" htmlFor="an-email">
          Email
        </label>
        <input
          ref={inputRef}
          id="an-email"
          className="an-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={data.email}
          onChange={(e) => update('email', e.target.value)}
          required
        />
        <p className="an-help">
          We&apos;ll only email you once — when the Android app launches.
        </p>
        {error && <p className="an-error">{error}</p>}
        <div className="an-actions">
          <button
            type="button"
            className="an-btn an-btn--ghost"
            onClick={back}
            disabled={busy}
          >
            Back
          </button>
          <button
            type="submit"
            className="an-btn an-btn--primary"
            disabled={busy}
          >
            {busy ? 'Adding…' : 'Notify me'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="an">
      <header className="an-top">
        <Link className="an-brand" to="/" aria-label="Fampack home">
          <img className="an-logo" src="/Logo.svg" alt="" />
          <span className="an-brand__name">Fampack</span>
        </Link>
        {showDots && <Dots total={RESERVE_STEPS.length} active={step} />}
      </header>
      <main className="an-main">
        <div className="an-stack">
          {body}
          {!done && <AgreementLine />}
        </div>
      </main>
    </div>
  );
}
